"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ForumMapper = void 0;
const forum_1 = require("../models/forum");
const mapper_1 = require("./mapper");
const postRating_1 = require("./postRating");
class ForumMapper {
    /** Сервер отдаёт путь от текущего раздела к корню; UI — корень → текущий (Breadcrumb.cs). */
    static mapBreadcrumbs(raw) {
        if (!Array.isArray(raw))
            return [];
        return mapper_1.PdaMapper.mapList(raw, ForumMapper.PathItemSchema).reverse();
    }
    static mapPoll(data) {
        if (data == null)
            return null;
        if (!Array.isArray(data) || data.length === 0)
            return null;
        const title = mapper_1.PdaMapper.cleanString(data[0]);
        let totalVotes = 0;
        let state = 0;
        let questionsRaw = [];
        if (Array.isArray(data[1])) {
            // Legacy: [title, question1, question2, ...]
            questionsRaw = data.slice(1).filter((item) => Array.isArray(item));
        }
        else if (Array.isArray(data[3])) {
            // Current: [title, totalVotes, state, [questions]]
            totalVotes = typeof data[1] === 'number' ? data[1] : 0;
            state = typeof data[2] === 'number' ? data[2] : 0;
            questionsRaw = data[3];
        }
        else if (typeof data[1] === 'number' && Array.isArray(data[2])) {
            // Variant: [title, totalVotes, [questions]]
            totalVotes = data[1];
            questionsRaw = data[2];
        }
        const questions = [];
        for (const item of questionsRaw) {
            if (!Array.isArray(item) || item.length < 3)
                continue;
            const text = mapper_1.PdaMapper.cleanString(item[0]);
            const multiple = typeof item[1] === 'number' ? item[1] !== 0 : false;
            const optionsRaw = item[2];
            const votesRaw = item[3];
            const options = [];
            if (Array.isArray(optionsRaw)) {
                for (let i = 0; i < optionsRaw.length; i++) {
                    const label = mapper_1.PdaMapper.cleanString(optionsRaw[i]);
                    const votes = Array.isArray(votesRaw) && typeof votesRaw[i] === 'number'
                        ? votesRaw[i]
                        : 0;
                    options.push({ label, votes });
                }
            }
            if (text || options.length > 0) {
                questions.push({ text, multiple, options });
            }
        }
        if (!title && questions.length === 0)
            return null;
        return {
            title: title || 'Опрос',
            totalVotes,
            state,
            canVote: state === 0,
            questions,
            questionsCount: questions.length,
        };
    }
    static mapForumIndex(data) {
        const flatItems = mapper_1.PdaMapper.mapList(data[0], this.ForumItemSchema);
        const categories = [];
        let currentCategory = null;
        for (const item of flatItems) {
            if ((0, forum_1.hasForumFlag)(item.flags, forum_1.ForumFlags.MARKER)) {
                currentCategory = {
                    id: item.id,
                    title: item.title,
                    flags: item.flags,
                    subforums: []
                };
                categories.push(currentCategory);
            }
            else {
                if (currentCategory) {
                    currentCategory.subforums.push(item);
                }
                else {
                    categories.push({
                        id: item.id,
                        title: item.title,
                        flags: item.flags,
                        subforums: [item]
                    });
                }
            }
        }
        return categories;
    }
    static mapTopicsList(data) {
        return mapper_1.PdaMapper.mapList(data, this.TopicSchema);
    }
    static mapAnnouncement(id, data) {
        return {
            id,
            title: mapper_1.PdaMapper.cleanString(data[0]),
            content: mapper_1.PdaMapper.cleanString(data[1]),
        };
    }
    static mapForumFull(data) {
        const schema = {
            id: 1,
            title: { index: 2, map: (v) => mapper_1.PdaMapper.cleanString(v) },
            flags: 3,
            globalAnnouncement: { index: 4, map: (v) => mapper_1.PdaMapper.cleanString(v) },
            topicsCount: 7,
            breadcrumbs: { index: 0, map: (v) => ForumMapper.mapBreadcrumbs(v) },
            announcements: { index: 5, list: ForumMapper.AnnouncementSchema },
            subforums: { index: 6, list: ForumMapper.SubforumSchema },
            topics: { index: 8, list: ForumMapper.TopicSchema }
        };
        return mapper_1.PdaMapper.map(data, schema);
    }
    static mapForumInfo(data) {
        const schema = {
            id: 1,
            title: 2,
            description: 3,
            flags: 4,
            rules: 5,
            subforumsCount: 6,
            topicsCount: 7,
            postsCount: 8,
            path: { index: 0, map: (v) => ForumMapper.mapBreadcrumbs(v) },
            moderators: { index: 9, list: ForumMapper.ModeratorSchema }
        };
        return mapper_1.PdaMapper.map(data, schema);
    }
    static mapWhoIsReading(data) {
        const schema = {
            guests: 0,
            hidden: 1,
            members: { index: 2, list: ForumMapper.EntityMemberSchema }
        };
        return mapper_1.PdaMapper.map(data, schema);
    }
    static mapTopicPosts(data) {
        return mapper_1.PdaMapper.map(data, ForumMapper.TopicResultSchema);
    }
    static mapForumFormField(data) {
        const type = data[0];
        const field = {
            type,
            label: mapper_1.PdaMapper.cleanString(data[1]),
            description: mapper_1.PdaMapper.cleanString(data[2]),
            placeholder: mapper_1.PdaMapper.cleanString(data[3]),
            required: (data[4] & 1) === 1,
            readOnly: (data[4] & 4) === 4,
            uploadable: (data[4] & 2) === 2,
            value: data[5]
        };
        switch (type) {
            case 'text':
                field.lines = data[6];
                field.maxLength = data[7];
                break;
            case 'checkbox_list':
            case 'dropdown':
                field.options = data[6];
                break;
            case 'upload_box':
                field.extensions = data[7];
                break;
        }
        return field;
    }
    static mapForumForm(data) {
        // For 'fw' command, fields are typically at index 2
        // data[0] is often the command status (e.g. 5000)
        const fieldsData = (Array.isArray(data[2]) ? data[2] : data[0]) || [];
        return {
            fields: fieldsData.map(f => this.mapForumFormField(f))
        };
    }
    /** Ответ fw action=1: data[0] = [?, ?, content, attachments[]]. */
    static mapTemplatePreview(data) {
        const template = Array.isArray(data) && Array.isArray(data[0])
            ? data[0]
            : (Array.isArray(data) ? data : []);
        return {
            content: mapper_1.PdaMapper.cleanString(template[2]),
            attachments: ForumMapper.mapPreviewAttachments(template[3]),
        };
    }
    static mapPostAttachments(raw) {
        if (!Array.isArray(raw)) {
            return [];
        }
        return raw
            .map((item) => {
            const row = item;
            const type = typeof row[1] === 'number' ? row[1] : 0;
            const isImage = type === 1;
            const name = mapper_1.PdaMapper.cleanString(row[2]) || `file-${row[0]}`;
            // Файлы: [4] = downloads, только если массив длиннее 4 (сервер режет поле без прав).
            // Картинки: [4] = thumb URL, downloads при необходимости в [7] (в UI не показываем).
            let downloads;
            if (isImage) {
                downloads = typeof row[7] === 'number' ? row[7] : undefined;
            }
            else if (row.length > 4 && typeof row[4] === 'number') {
                downloads = row[4];
            }
            return {
                id: Number(row[0]) || 0,
                type,
                isImage,
                name,
                size: typeof row[3] === 'number' ? row[3] : undefined,
                thumbnailUrl: isImage && typeof row[4] === 'string' ? row[4] : undefined,
                width: typeof row[5] === 'number' ? row[5] : undefined,
                height: typeof row[6] === 'number' ? row[6] : undefined,
                downloads,
            };
        })
            .filter((item) => item.id > 0);
    }
    static mapPreviewAttachments(raw) {
        if (!Array.isArray(raw)) {
            return [];
        }
        return raw
            .map((item) => {
            const row = item;
            return {
                id: Number(row[0]) || 0,
                type: Number(row[1]) || 0,
                name: mapper_1.PdaMapper.cleanString(row[2]),
                size: typeof row[3] === 'number' ? row[3] : undefined,
            };
        })
            .filter((item) => item.id > 0);
    }
}
exports.ForumMapper = ForumMapper;
ForumMapper.ForumItemSchema = {
    id: 0,
    title: { index: 1, map: (v) => mapper_1.PdaMapper.cleanString(v) },
    flags: 2,
    url: 3
};
ForumMapper.PathItemSchema = {
    type: 0,
    id: 1,
    name: 2
};
ForumMapper.AnnouncementSchema = {
    id: 0,
    title: 1
};
ForumMapper.SubforumSchema = {
    id: 0,
    title: { index: 1, map: (v) => mapper_1.PdaMapper.cleanString(v) },
    flags: 2,
    url: 3
};
ForumMapper.TopicSchema = {
    id: 0,
    title: { index: 1, map: (v) => mapper_1.PdaMapper.cleanString(v) },
    subtitle: { index: 2, map: (v) => mapper_1.PdaMapper.cleanString(v) },
    bitmask: 3,
    replies: 4,
    lastPostTs: { index: 5, map: (v) => mapper_1.PdaMapper.safeIsoDate(v) },
    lastUserId: 6,
    lastUserName: 7
};
ForumMapper.ModeratorSchema = {
    id: 0,
    name: 1,
    colorIndex: 2
};
ForumMapper.EntityMemberSchema = {
    id: 0,
    name: 1,
    groupId: 2,
    hidden: { index: 3, map: (v) => v === 1 },
};
ForumMapper.PostSchema = {
    id: 0,
    bitmask: 1,
    author: {
        index: [2, 3, 4, 6, 9, 10],
        map: (v) => ({
            id: v[0], name: v[1], groupId: v[2], reputation: v[3],
            avatar: v[4], signature: v[5]
        })
    },
    date: { index: 7, map: (v) => mapper_1.PdaMapper.safeIsoDate(v) },
    content: 8,
    attachments: { index: 11, map: (v) => ForumMapper.mapPostAttachments(v) },
    ratingRaw: 12,
    rating: { index: 12, map: (v) => (0, postRating_1.decodePostRating)(v).value },
    ratingVisible: { index: 12, map: (v) => (0, postRating_1.decodePostRating)(v).visible },
    userPostVote: { index: 12, map: (v) => (0, postRating_1.decodePostRating)(v).userVote },
    flags: 5,
    edit: {
        index: [13, 14, 15, 16],
        map: (v) => {
            if (!v[0])
                return undefined;
            return {
                date: mapper_1.PdaMapper.safeIsoDate(v[0]),
                userName: v[1],
                reason: v[2],
                userId: v[3],
            };
        },
    }
};
ForumMapper.TopicResultSchema = {
    // themeId: 0, // In raw 0 is breadcrumbs
    breadcrumbs: { index: 0, map: (v) => ForumMapper.mapBreadcrumbs(v) },
    topicId: 1,
    title: { index: 2, map: (v) => mapper_1.PdaMapper.cleanString(v) },
    subtitle: { index: 3, map: (v) => mapper_1.PdaMapper.cleanString(v) },
    bitmask: 4,
    lastPostTs: { index: 5, map: (v) => mapper_1.PdaMapper.safeIsoDate(v) },
    lastUserId: 6,
    lastUserName: 7,
    curator: {
        index: [8, 9],
        map: (v) => ({ id: v[0], name: v[1] })
    },
    poll: { index: 10, map: (v) => ForumMapper.mapPoll(v) },
    replies: 11,
    posts: { index: 12, list: ForumMapper.PostSchema },
    postTemplateName: {
        index: 13,
        map: (v) => {
            if (!Array.isArray(v) || v.length === 0)
                return undefined;
            const first = v[0];
            if (typeof first !== 'string')
                return undefined;
            const cleaned = mapper_1.PdaMapper.cleanString(first);
            return cleaned.trim() ? cleaned : undefined;
        },
    },
};
